import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetPlansListDocument,
  CreatePlanDocument,
  type GetPlansListQuery,
  type CreatePlanMutation,
  type CreatePlanMutationVariables,
} from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { useNotification } from '@/app/contexts/NotificationContext';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';

interface Row extends Record<string, unknown> {
  id: string;
  name: string;
  code: string;
  contractTermMonths: number;
  amountNet: number;
  amountGross: number;
  createdAt: string;
  updatedAt: string;
}

export default function Plans() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatMoney = (n: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(n);
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [contractTermMonths, setContractTermMonths] = useState('12');
  const [amountNet, setAmountNet] = useState('');
  const [amountGross, setAmountGross] = useState('');

  const { data, loading, refetch } = useQuery<GetPlansListQuery>(GetPlansListDocument, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE },
  });

  const [createPlan, { loading: creating }] = useMutation<
    CreatePlanMutation,
    CreatePlanMutationVariables
  >(CreatePlanDocument);

  const nodes = data?.plans?.nodes ?? [];
  const rows: Row[] = nodes.map((n) => ({
    id: n.id,
    name: n.name,
    code: n.code,
    contractTermMonths: n.contractTermMonths,
    amountNet: n.amountNet,
    amountGross: n.amountGross,
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
  }));

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 160 },
    { field: 'code', headerName: t('masterData.code'), width: 120 },
    { field: 'contractTermMonths', headerName: t('masterData.termMonths'), width: 120 },
    {
      field: 'amountNet',
      headerName: t('common.net'),
      width: 120,
      valueFormatter: (v: number) => formatMoney(v),
    },
    {
      field: 'amountGross',
      headerName: t('common.gross'),
      width: 120,
      valueFormatter: (v: number) => formatMoney(v),
    },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 155,
      valueFormatter: (v: string) => formatDateTime(v),
    },
    {
      field: 'updatedAt',
      headerName: t('common.updated'),
      width: 155,
      valueFormatter: (v: string) => formatDateTime(v),
    },
  ];

  const handleCreate = async () => {
    const net = parseFloat(amountNet.replace(',', '.'));
    const gross = parseFloat(amountGross.replace(',', '.'));
    const months = parseInt(contractTermMonths, 10);
    if (Number.isNaN(net) || Number.isNaN(gross) || Number.isNaN(months)) {
      showError(t('errors.generic'));
      return;
    }
    try {
      const res = await createPlan({
        variables: {
          input: {
            name,
            code,
            contractTermMonths: 12,
            amountNet: net,
            amountGross: gross,
            intervalMonths: months,
            status: 'PUBLISHED',
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (!res.data?.createPlan?.plan) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('masterData.created'));
      setOpen(false);
      setName('');
      setCode('');
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h4">{t('masterData.plans')}</Typography>
        {isSuperAdmin(user) && (
          <Button variant="contained" onClick={() => setOpen(true)}>
            {t('masterData.createPlan')}
          </Button>
        )}
      </Box>

      <DataTable<Row>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={rows.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
        onRowClick={(row) => navigate(`/plans/${row.id}`)}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('masterData.createPlan')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label={t('masterData.code')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label={t('masterData.termMonths')}
            value={contractTermMonths}
            onChange={(e) => setContractTermMonths(e.target.value)}
            type="number"
            fullWidth
          />
          <TextField
            label={t('common.net')}
            value={amountNet}
            onChange={(e) => setAmountNet(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('common.gross')}
            value={amountGross}
            onChange={(e) => setAmountGross(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !name.trim() || !code.trim()}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
